import { useMemo } from 'react';
import { useApplicationContext } from 'ApplicationContext';
import {
    ArticleCreate,
    ArticleEdit,
    LabelWinExportRequest,
    LeistungenRequest,
    OptionGroupDto,
    PriceTier,
    ProjectCalculateRequest,
    RequirementCreate,
    RequirementEdit,
    StandortTyp,
} from 'apiClient/Api';

// Die Kalkulator-Controller-Actions liefern `IActionResult` ohne `[ProducesResponseType]`, daher
// erzeugt der Swagger-Generator für ihre Antworten nur `File`. Die Request-DTOs werden aus
// `apiClient/Api` wiederverwendet; die fehlenden Antwort-Typen sind hier (camelCase wie die
// System.Text.Json-Serialisierung; Enums als Klartext) nachgebildet und 1:1 aus den C#-Records
// des Office-Kalkulators übernommen.

export type InputKind = 'Spin' | 'Checkbox' | 'Radio';
export type LogLevelName = 'Standard' | 'Info' | 'Warning' | 'Error';
export type PositionKind = 'Visible' | 'Hidden' | 'GroupHeader' | 'Summary';
export type IntegritySeverity = 'Error' | 'Warning';

export interface ImageCrop {
    x: number;
    y: number;
    width: number;
    height: number;
    aspectRatio: number;
}

/** Bild einer Anforderung/Option im Eingabebaum (Quelle: referenzierter Artikel). */
export interface RequirementImage {
    articleNumber: string;
    name: string;
    titel: string;
    beschreibung: string;
    haupt: boolean;
    crop: ImageCrop | null;
}

/** Eine zur Auswahl angebotene Option innerhalb einer Optionsgruppe. */
export interface RequirementOption {
    requirementId: string;
    description: string;
    defaultAnzahl: number;
    /** Beim Anlegen der Instanz vorbelegen (default="1"). */
    isDefault: boolean;
    /** Im Angebotstext gedruckt (im_angebot != "0"). */
    inOffer: boolean;
    /** Ersetzt den Eltern-Text im Angebot (ersetzt_beschreibung="1"). */
    overridesDescription: boolean;
    images: RequirementImage[];
    /** Verschachtelte Optionsgruppen dieser Option. */
    optionGroups: RequirementOptionGroup[];
}

/** Eine logische Gruppe von Optionen (ersetzt das alte r_gruppe). */
export interface RequirementOptionGroup {
    /** Überschrift der Gruppe (kann leer sein). */
    title: string | null;
    /** Eingabe-Widget der Gruppe. */
    inputKind: InputKind;
    /** Gegenseitiger Ausschluss der Optionen (Radio immer; Spin via exklusiv). */
    exclusive: boolean;
    /** Muss genau eine Option gewählt sein? */
    mandatory: boolean;
    options: RequirementOption[];
}

export interface RequirementItem {
    id: string;
    description: string;
    inputKind: InputKind;
    group: string;
    articleGroup: string;
    implicit: boolean;
    catalogs: string[];
    images: RequirementImage[];
    /** Standort-Typen, für die die Anforderung wählbar ist (Whitelist); leer/fehlend = alle. */
    availableFor?: StandortTyp[];
    /** Optionsgruppen (ersetzt das frühere flache `options`). */
    optionGroups: RequirementOptionGroup[];
}

export interface RequirementGroup {
    title: string;
    path: string;
    items: RequirementItem[];
    groups: RequirementGroup[];
}

export interface Rubric {
    code: string;
    label: string;
}

export interface PriceBand {
    quantity: number;
    unitPrice: number;
}

/** Bild einer Angebotsposition (relative `ref`, kein fertiger URL). */
export interface PositionImage {
    ref: string;
    titel: string;
    beschreibung: string;
    haupt: boolean;
    crop: ImageCrop | null;
}

export interface OfferPosition {
    kind: PositionKind;
    articleGroup: string;
    articleNumber: string;
    quantity: number;
    description: string;
    unitPrice: number;
    totalPrice: number;
    priceBreakdown: PriceBand[];
    setName: string;
    isOption: boolean;
    area: string;
    visible: boolean;
    images: PositionImage[];
}

export interface OfferSummary {
    basisSystem: number;
    softwareLoesung: number;
    bedieneinheiten: number;
    wartung: number;
    gesamt: number;
}

export interface RequirementCost {
    requirementId: string;
    description: string;
    cost: number;
}

export interface RequirementSummaryEntry {
    quantity: number;
    description: string;
    isOption: boolean;
    area: string;
}

export interface LogEntry {
    level: LogLevelName;
    message: string;
}

export interface OfferResult {
    positions: OfferPosition[];
    summary: OfferSummary;
    requirementCosts: RequirementCost[];
    requirementSummary: RequirementSummaryEntry[];
    warnings: string[];
    log: LogEntry[];
}

export interface OfferFragments {
    html: string;
    markdown: string;
    rtf: string;
    tableHtml: string;
    tableMarkdown: string;
    tableRtf: string;
}

export interface CalculateResponse {
    result: OfferResult;
    fragments: OfferFragments;
}

export interface LvFragment {
    html: string;
    markdown: string;
}

export interface LeistungenResponse {
    leistungsangabe: LvFragment;
    lv: LvFragment;
    availableDomains: string[];
}

export interface LabelWinStatusResponse {
    enabled: boolean;
}

export interface LabelWinSyncResponse {
    enabled: boolean;
    count: number;
}

export interface LabelWinExportResponse {
    written: number;
    target: string;
}

export interface IntegrityIssue {
    severity: IntegritySeverity;
    where: string;
    message: string;
    category: string | null;
    articleNumber: string | null;
}

export interface ArticleView {
    articleNumber: string;
    articleGroup: string;
    typ: string;
    beschreibung: string;
    vkf1: number;
    netto1: number;
    lohnNetto: number;
    lohnMinuten: number;
    priceMissing: boolean;
    missingArticleDir: boolean;
    missingLeistung: boolean;
    nullpreisOk: boolean;
    lvOff: boolean;
    dirOff: boolean;
    staffel: PriceTier[] | null;
}

export interface ArtikelRefView {
    aNr: string | null;
    gId: string | null;
}

export interface ParameterView {
    key: string;
    value: string;
    type: string | null;
}

export interface RessourceRefView {
    name: string;
    anzahl: number;
    per: number | null;
    articleRefs: ArtikelRefView[];
}

export interface RequirementView {
    id: string;
    category: string;
    articleGroup: string;
    titel: string;
    details: string | null;
    eingabe: string | null;
    implicit: boolean;
    /** Lizenz-Kennung des Software-Moduls (license_flag) oder null. */
    licenseFlag: string | null;
    /** Eingabeart-Override der Anforderung (eingabe_typ); null = Gruppen-Default. */
    eingabeTyp: InputKind | null;
    /** Standort-Typen, für die die Anforderung wählbar ist (Whitelist); leer = alle. */
    availableFor: StandortTyp[];
    parameters: ParameterView[];
    articleRefs: ArtikelRefView[];
    resourceRefs: RessourceRefView[];
    /** Optionsgruppen (ersetzt das frühere flache `options`); identisch zur OptionGroupDto. */
    optionGroups: OptionGroupDto[];
}

export interface ArticleImageView {
    name: string;
    titel: string;
    beschreibung: string;
    haupt: boolean;
    imageUrl: string;
    crop: ImageCrop | null;
}

export interface DeleteBlocked {
    id: string;
    referencedBy: string[];
}

/**
 * Typisierte Fassade über den generierten `api.calculator`-Client. Castet die als `File`
 * typisierten Antworten auf die echten DTOs und kapselt das authentifizierte Nachladen der
 * Artikel-Bilddateien (Bearer-geschützt → Blob statt direkter `<img src>`).
 */
export const useCalculatorApi = () => {
    const { api } = useApplicationContext();

    return useMemo(() => {
        const cast = <T,>(p: Promise<{ data: unknown }>): Promise<T> => p.then((r) => r.data as unknown as T);

        return {
            getRequirements: () => cast<RequirementGroup>(api.calculator.calculatorGetRequirements()),
            listRubrics: () => cast<Rubric[]>(api.calculator.calculatorListRubrics()),
            calculateProject: (req: ProjectCalculateRequest) =>
                cast<CalculateResponse>(api.calculator.calculatorCalculateProject(req)),
            generateLeistungen: (req: LeistungenRequest) =>
                cast<LeistungenResponse>(api.calculator.calculatorGenerateLeistungen(req)),

            labelwinStatus: () => cast<LabelWinStatusResponse>(api.calculator.calculatorLabelWinStatus()),
            labelwinSync: () => cast<LabelWinSyncResponse>(api.calculator.calculatorLabelWinSync()),
            labelwinExport: (req: LabelWinExportRequest) =>
                cast<LabelWinExportResponse>(api.calculator.calculatorLabelWinExport(req)),

            validate: () => cast<IntegrityIssue[]>(api.calculator.calculatorValidate()),

            // Stammdaten serverseitig neu aus den XML-Quellen laden.
            reloadData: () => api.calculator.calculatorReload(),

            // Editor – Katalog
            listArticles: () => cast<ArticleView[]>(api.calculator.calculatorListArticles()),
            listArticleGroups: () => cast<string[]>(api.calculator.calculatorListArticleGroups()),
            updateArticle: (aNr: string, edit: ArticleEdit) => api.calculator.calculatorUpdateArticle(aNr, edit),
            createArticle: (create: ArticleCreate) => api.calculator.calculatorCreateArticle(create),
            deleteArticle: (aNr: string) => api.calculator.calculatorDeleteArticle(aNr),

            // Editor – Anforderungen
            listRequirements: () => cast<RequirementView[]>(api.calculator.calculatorListRequirements()),
            updateRequirement: (id: string, edit: RequirementEdit) =>
                api.calculator.calculatorUpdateRequirement(id, edit),
            createRequirement: (create: RequirementCreate) => api.calculator.calculatorCreateRequirement(create),
            deleteRequirement: (id: string) => api.calculator.calculatorDeleteRequirement(id),

            // Bilder (authentifiziert als Blob)
            getArticleImages: (aNr: string) => cast<ArticleImageView[]>(api.calculator.calculatorGetArticleImages(aNr)),
            loadImageBlob: async (aNr: string, name: string): Promise<Blob> => {
                const r = await api.calculator.calculatorGetArticleImageFile(aNr, name, { format: 'blob' });
                return r.data as unknown as Blob;
            },
        };
    }, [api]);
};

export type CalculatorApi = ReturnType<typeof useCalculatorApi>;
